import { useEffect, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { capitalize, debounce } from "lodash"
import { Switch, FormControlLabel } from "@mui/material"

import { CHART_TYPE, ORDINAL_STRINGS, SECTION_TYPE } from "@/app/report/common/constants"
import { SessionDetailsSettings, SettingSectionProps } from "@/app/report/common/type"
import TextField from "@/components/form/textField/TextField"
import RadioGroup from "@/components/form/radioGroup/RadioGroup"
import Selector from "@/components/form/selector/Selector"
import ColorPicker from "@/app/report/components/setting/ColorPicker"
import IconButton from "@/components/button/IconButton"
import MetricSetting from "@/app/report/components/setting/MetricSetting"
import SettingSectionItem from "@/app/report/components/setting/SettingSectionItem"
import SortSelector from "@/app/report/components/setting/SortSelector"
import Divider from "@/components/divider/Divider"
import { colors } from "@/components/colors"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook.ts"

const DEFAULT_SESSION_DETAILS: SessionDetailsSettings = {
  calcType: "average",
  yAxis: [
    {
      metric: "totalDistance",
      chartType: "bar",
      color: colors.blue[700],
      manualScaleActive: false,
      manualScaleValue: null,
    },
  ],
  dataLabel: "hideValues",
  decimalPlace: 1,
}

export default function SessionDetailsSection({
  settings,
  tempTemplate,
  setTempTemplate,
  sectionIndex,
}: SettingSectionProps<SessionDetailsSettings[]> & { sectionIndex: number }) {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()
  const [tempSessionItems, setTempSessionItems] =
    useState<SessionDetailsSettings>(DEFAULT_SESSION_DETAILS)
  const [manualScaleValues, setManualScaleValues] = useState<{
    [yAxisIndex: number]: string
  }>({})

  const getSortMenuOptions = () => {
    const valueChildren = tempSessionItems.yAxis.map((yAxis) => ({
      key: yAxis.metric,
      label: translateKey(yAxis.metric),
      color: yAxis.color,
      children: [
        {
          key: `${yAxis.metric}-asc`,
          label: t("lowToHigh", { defaultValue: "Low to High" }),
          value: { key: yAxis.metric, order: "asc" as const },
        },
        {
          key: `${yAxis.metric}-desc`,
          label: t("highToLow", { defaultValue: "High to Low" }),
          value: { key: yAxis.metric, order: "desc" as const },
        },
      ],
    }))

    return [
      {
        key: "time",
        label: t("time"),
        children: [
          {
            key: "time-asc",
            label: t("lowToHigh", { defaultValue: "Low to High" }),
            value: { key: "time", order: "asc" as const },
          },
          {
            key: "time-desc",
            label: t("highToLow", { defaultValue: "High to Low" }),
            value: { key: "time", order: "desc" as const },
          },
        ],
        is3D: false,
      },
      {
        key: "value",
        label: t("value"),
        children: valueChildren,
        is3D: true,
      },
      {
        key: "sessionName",
        label: t("sessionName"),
        children: [
          {
            key: "sessionName-asc",
            label: t("aToZ", { defaultValue: "A - Z" }),
            value: { key: "sessionName", order: "asc" as const },
          },
          {
            key: "sessionName-desc",
            label: t("zToA", { defaultValue: "Z - A" }),
            value: { key: "sessionName", order: "desc" as const },
          },
        ],
        is3D: false,
      },
    ]
  }

  const debouncedUpdateManualScale = useCallback(
    debounce((yAxisIndex: number, value: number | null) => {
      setTempSessionItems((prev) => {
        const updated = { ...prev }
        updated.yAxis[yAxisIndex] = {
          ...updated.yAxis[yAxisIndex],
          manualScaleValue: value,
        }

        return updated
      })
    }, 200),
    [],
  )

  useEffect(() => {
    setTempSessionItems(settings[0])
  }, [settings])

  useEffect(() => {
    setTempTemplate((prev) => {
      if (!prev) return null
      const updatedContent = [...prev.content]
      updatedContent[sectionIndex] = {
        ...updatedContent[sectionIndex],
        settings: [tempSessionItems],
      }
      return { ...prev, content: updatedContent }
    })
  }, [tempSessionItems, sectionIndex, setTempTemplate])

  return (
    <div className="DailySettingSessionDetailsSectionCont">
      <SettingSectionItem>
        <div key="display" className="daily-setting-session-details-display-row">
          <p className="label-text">{t("display")}</p>
          <div className="daily-setting-session-details-display-options-wrapper">
            {/* 값 표시·소수점 — #185(661fa0c6)에서 지워졌다가 복구 (2026-10-15 핫픽스) */}
            <Selector
              label={t("dataLabels")}
              options={[
                { value: "hideValues", label: t("hideValues") },
                { value: "abovePlots", label: t("abovePlots") },
                { value: "separateTable", label: t("separateTable") },
              ]}
              value={{
                value: tempSessionItems.dataLabel || "hideValues",
                label: t(tempSessionItems.dataLabel || "hideValues"),
              }}
              onChange={(_: unknown, newVal: { value: string; label: string } | null) => {
                if (!newVal) {
                  return
                }

                setTempSessionItems((prev) => {
                  const updated = { ...prev }
                  updated.dataLabel = newVal.value

                  return updated
                })
              }}
              placeholder={t("select")}
            />
            <Selector
              label={t("decimalPlaces")}
              disabled={(tempSessionItems.dataLabel || "hideValues") === "hideValues"}
              options={[
                { value: "0", label: t("none") },
                { value: "1", label: "0.1" },
                { value: "2", label: "0.01" },
              ]}
              value={(() => {
                const decimalValue = tempSessionItems.decimalPlace?.toString() || "1"
                const options = [
                  { value: "0", label: t("none") },
                  { value: "1", label: "0.1" },
                  { value: "2", label: "0.01" },
                ]
                const foundOption = options.find((opt) => opt.value === decimalValue)

                return foundOption || { value: "1", label: "0.1" }
              })()}
              onChange={(_: unknown, newVal: { value: string; label: string } | null) => {
                if (!newVal) {
                  return
                }

                setTempSessionItems((prev) => {
                  const updated = { ...prev }
                  updated.decimalPlace = parseInt(newVal.value)

                  return updated
                })
              }}
              placeholder={t("select")}
            />
            <div className="daily-setting-session-details-radio">
              <p className="label-text">{t("metricCalculationOption")}</p>
              <RadioGroup
                direction="row"
                items={[
                  { label: t("average"), value: "average" },
                  { label: t("total"), value: "total" },
                ]}
                value={tempSessionItems.calcType}
                onChange={(_, newVal) => {
                  if (!newVal) {
                    return
                  }
                  setTempSessionItems((prev) => {
                    const updated = { ...prev }
                    updated.calcType = newVal

                    return updated
                  })
                }}
              />
            </div>
          </div>
        </div>
        <Divider />
        <div className="daily-setting-session-details-name-row">
          <p className="label-text">{t("xAxis")}</p>
          <TextField
            label={t("name")}
            value={`${t("session")}, 1 min, 5 min, 15 min`}
            className="daily-setting-session-details-name"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
        </div>
        {tempSessionItems.yAxis.map((item, index) => {
          return (
            <div key={index} className="DailySettingSessionDetailsYAxisCont">
              <p className="label-text">{`${t(ORDINAL_STRINGS[index])} ${t("yAxis")}`}</p>
              <MetricSetting
                selectedMetric={item.metric}
                setSelectedMetric={(metric) => {
                  setTempSessionItems((prev) => ({
                    ...prev,
                    yAxis: prev.yAxis.map((y, i) => (i === index ? { ...y, metric } : y)),
                  }))
                }}
              />
              <div className="daily-setting-session-details-selector">
                <p className="label-text">{t("chartType")}</p>
                <Selector
                  options={Object.entries(CHART_TYPE).map(([, item]) => ({
                    value: item,
                    label: item === "stacked" ? t("stackedBar") : capitalize(item),
                  }))}
                  value={{
                    value: item.chartType,
                    label:
                      item.chartType === "stacked" ? t("stackedBar") : capitalize(item.chartType),
                  }}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props

                    if (!option) {
                      return null
                    }

                    return (
                      <li key={key} {...optionProps}>
                        <p>{option.label}</p>
                      </li>
                    )
                  }}
                  onChange={(_: unknown, { value }: { value: string; label: string }) => {
                    setTempSessionItems((prev) => {
                      const updated = { ...prev }
                      updated.yAxis[index] = {
                        ...updated.yAxis[index],
                        chartType: value,
                      }

                      return updated
                    })
                  }}
                  placeholder={t("select")}
                />
              </div>
              <div className="daily-setting-session-details-manual-scale-wrapper">
                <p className="daily-setting-session-details-manual-scale-title">
                  {t("manualScale")}
                </p>
                <FormControlLabel
                  control={
                    <Switch
                      checked={item.manualScaleActive || false}
                      onChange={(e) => {
                        setTempSessionItems((prev) => {
                          const updated = { ...prev }
                          updated.yAxis[index] = {
                            ...updated.yAxis[index],
                            manualScaleActive: e.target.checked,
                          }

                          return updated
                        })
                      }}
                      size="small"
                    />
                  }
                  label=""
                />
              </div>
              <div className="daily-setting-session-details-max-value-wrapper">
                <p className="daily-setting-session-details-max-value-title">{t("maxValue")}</p>
                <TextField
                  value={
                    manualScaleValues[index] !== undefined
                      ? manualScaleValues[index]
                      : item.manualScaleValue || ""
                  }
                  onChange={(e) => {
                    const inputValue = e.target.value

                    if (!/^\d*\.?\d*$/.test(inputValue) && inputValue !== "") {
                      return
                    }

                    setManualScaleValues((prev) => ({
                      ...prev,
                      [index]: inputValue,
                    }))

                    const numValue = inputValue === "" ? null : Number(inputValue)
                    debouncedUpdateManualScale(index, numValue)
                  }}
                  disabled={!item.manualScaleActive}
                />
              </div>
              <ColorPicker
                color={item.color}
                setColor={(color) =>
                  setTempSessionItems((prev) => {
                    const updated = { ...prev }
                    updated.yAxis[index] = {
                      ...updated.yAxis[index],
                      color: color,
                    }

                    return updated
                  })
                }
              />
              <div className="daily-setting-session-details-row-button">
                <IconButton
                  type="default"
                  size="md"
                  icon="subtract"
                  onClick={() =>
                    setTempSessionItems((prev) => ({
                      ...prev,
                      yAxis: prev.yAxis.filter((_, i) => i !== index),
                    }))
                  }
                  disabled={index === 0}
                />
                <div className="add-button-placeholder">
                  {index === tempSessionItems.yAxis.length - 1 && (
                    <IconButton
                      type="default"
                      size="md"
                      icon="add"
                      onClick={() =>
                        setTempSessionItems((prev) => ({
                          ...prev,
                          yAxis: [...prev.yAxis, DEFAULT_SESSION_DETAILS.yAxis[0]],
                        }))
                      }
                      disabled={index === 2}
                    />
                  )}
                </div>
              </div>
            </div>
          )
        })}
        <Divider />
        <div key="sort" className="daily-setting-session-details-sort-row">
          <p className="label-text">{t("sort")}</p>
          <SortSelector
            value={tempSessionItems.sortSetting || { key: "time", order: "asc" }}
            onChange={(value) => {
              setTempSessionItems((prev) => {
                const updated = { ...prev }
                updated.sortSetting = value
                return updated
              })
            }}
            options={getSortMenuOptions()}
          />
        </div>
      </SettingSectionItem>
    </div>
  )
}
